/**
 * Date utility functions for consistent date formatting across the application
 */
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

// Extend dayjs with custom parse format plugin
dayjs.extend(customParseFormat);

/**
 * Standard date format used throughout the application
 */
export const STANDARD_DATE_FORMAT = 'DD/MM/YYYY';

/**
 * API date format (ISO format)
 */
export const API_DATE_FORMAT = 'YYYY-MM-DD';

/**
 * Formats a date to the standard DD/MM/YYYY format
 * @param date - Date to format (string, Date, Dayjs, or null/undefined)
 * @returns Formatted date string or '-' if invalid
 */
export const formatDateConsistent = (date: string | Date | Dayjs | null | undefined): string => {
  if (!date) return '-';
  
  try {
    const dayjsDate = dayjs(date);
    if (!dayjsDate.isValid()) {
      console.warn('Invalid date provided to formatDateConsistent:', date);
      return '-';
    }
    return dayjsDate.format(STANDARD_DATE_FORMAT);
  } catch (error) {
    console.warn('Date formatting error:', error, 'for date:', date);
    return '-';
  }
};

/**
 * Formats a date for API consumption (YYYY-MM-DD format)
 * @param date - Date to format (string, Date, Dayjs, or null/undefined)
 * @returns Formatted date string or null if invalid
 */
export const formatDateForAPI = (date: string | Date | Dayjs | null | undefined): string | null => {
  if (!date) return null;
  
  try {
    const dayjsDate = dayjs(date);
    if (!dayjsDate.isValid()) {
      console.warn('Invalid date provided to formatDateForAPI:', date);
      return null;
    }
    return dayjsDate.format(API_DATE_FORMAT);
  } catch (error) {
    console.warn('Date formatting error:', error, 'for date:', date);
    return null;
  }
};

/**
 * Parses a date string in DD/MM/YYYY format to a Dayjs object
 * @param dateString - Date string in DD/MM/YYYY format
 * @returns Dayjs object or null if invalid
 */
export const parseDateFromStandard = (dateString: string): Dayjs | null => {
  if (!dateString || dateString === '-') return null;
  
  try {
    const parsed = dayjs(dateString, STANDARD_DATE_FORMAT, true);
    return parsed.isValid() ? parsed : null;
  } catch (error) {
    console.warn('Date parsing error:', error, 'for date string:', dateString);
    return null;
  }
};

/**
 * Validates if a date string is in the correct DD/MM/YYYY format
 * @param dateString - Date string to validate
 * @returns true if valid, false otherwise
 */
export const isValidDateFormat = (dateString: string): boolean => {
  if (!dateString) return false;
  
  const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
  if (!dateRegex.test(dateString)) return false;
  
  const parsed = parseDateFromStandard(dateString);
  return parsed !== null;
};

/**
 * Gets a date range for the last N days
 * @param days - Number of days to go back (default: 30)
 * @returns Object with fromDate and toDate as Dayjs objects
 */
export const getLastNDaysRange = (days: number = 30) => {
  const toDate = dayjs().startOf('day');
  const fromDate = toDate.subtract(days - 1, 'days'); // -1 because we include today
  return { fromDate, toDate };
};

/**
 * Formats a date range for display
 * @param startDate - Start date
 * @param endDate - End date
 * @returns Formatted date range string
 */
export const formatDateRange = (
  startDate: string | Date | Dayjs | null | undefined,
  endDate: string | Date | Dayjs | null | undefined
): string => {
  const formattedStart = formatDateConsistent(startDate);
  const formattedEnd = formatDateConsistent(endDate);
  
  if (formattedStart === '-' && formattedEnd === '-') return '-';
  if (formattedStart === formattedEnd) return formattedStart;
  
  return `${formattedStart} - ${formattedEnd}`;
};

// Default export for module compatibility
export default {
  formatDateConsistent,
  formatDateForAPI,
  parseDateFromStandard,
  isValidDateFormat,
  getLastNDaysRange,
  formatDateRange,
  STANDARD_DATE_FORMAT,
  API_DATE_FORMAT
};